import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccount } from "../account/AccountContext";
import AppShell from "../components/AppShell";
import { Btn, Eyebrow } from "../components/Primitives";
import SignupConsent, { isUnder18, UNDERAGE_MESSAGE } from "../components/SignupConsent";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
    const { finishGoogleAuth, completeGoogleConsent } = useAccount();
    const navigate = useNavigate();
    const processed = useRef(false);
    const [error, setError] = useState(null);
    const [pending, setPending] = useState(null); // { pending_token, profile }
    const [dob, setDob] = useState("");
    const [agreed, setAgreed] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [consentError, setConsentError] = useState(null);

    useEffect(() => {
        if (processed.current) return;
        processed.current = true;
        const hash = window.location.hash || "";
        const m = hash.match(/session_id=([^&]+)/);
        if (!m) {
            setError("Missing Google session.");
            return;
        }
        (async () => {
            try {
                const result = await finishGoogleAuth(decodeURIComponent(m[1]));
                window.history.replaceState({}, document.title, window.location.pathname);
                if (result?.needs_consent) {
                    setPending(result);
                    return;
                }
                navigate("/", { replace: true });
            } catch (e) {
                setError(e?.response?.data?.detail || "Google sign-in failed. Please try again.");
            }
        })();
    }, [finishGoogleAuth, navigate]);

    const submitConsent = async () => {
        if (isUnder18(dob)) { setConsentError(UNDERAGE_MESSAGE); return; }
        setSubmitting(true);
        setConsentError(null);
        try {
            await completeGoogleConsent(pending.pending_token, dob, agreed);
            navigate("/", { replace: true });
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setConsentError(typeof detail === "string" ? detail : "Could not complete sign-up. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AppShell>
            <div className="flex-1 flex flex-col justify-center px-7 py-10" data-testid="auth-callback-screen">
                {pending ? (
                    <div data-testid="google-consent-screen">
                        <Eyebrow>One last thing</Eyebrow>
                        <h1 className="mt-2 font-display text-4xl text-burgundy leading-tight">
                            <span className="italic">Before</span> we create your account.
                        </h1>
                        <p className="mt-3 text-sm text-ink-muted">
                            Signing up as <span className="text-burgundy font-medium">{pending.profile?.email}</span>
                        </p>
                        <div className="mt-7">
                            <SignupConsent dob={dob} setDob={(v) => { setDob(v); setConsentError(null); }} agreed={agreed} setAgreed={setAgreed} idPrefix="google" />
                        </div>
                        {consentError && (
                            <div data-testid="google-consent-error" className="mt-4 text-sm text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">
                                {consentError}
                            </div>
                        )}
                        <div className="mt-7">
                            <Btn
                                data-testid="google-consent-submit-btn"
                                onClick={submitConsent}
                                disabled={!agreed || !dob || isUnder18(dob) || submitting}
                                className={!agreed || !dob || isUnder18(dob) || submitting ? "opacity-50 cursor-not-allowed" : ""}
                            >
                                {submitting ? "Creating your account…" : "Create my account"}
                            </Btn>
                        </div>
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center">
                        <p className="font-display italic text-2xl text-burgundy">{error}</p>
                        <button onClick={() => navigate("/login")} className="mt-6 text-[11px] tracking-[0.28em] uppercase text-burgundy underline underline-offset-4">
                            ← Back to sign in
                        </button>
                    </div>
                ) : (
                    <p className="text-center font-display italic text-2xl text-burgundy">Signing you in…</p>
                )}
            </div>
        </AppShell>
    );
}
